import {FIELD_BIOME_TEXTURES} from '../../configs/fieldBiomeEffects';

/**
 * Текстуры эффектов подложек (см. scenes/FieldBiomeEffects). Картинок в public
 * нет — пиксели считаются кодом и регистрируются через textures.addUint8Array:
 * такая текстура переживает потерю GPU-контекста (подробно —
 * utils/sprites/generateOutlineTexture).
 *
 * ФОРМАТ: RGBA построчно сверху вниз, цвет НЕ умножен на альфу (подробно —
 * utils/effects/shotProjectileTextures). Размеры — степени двойки. Почти все
 * фигуры белые, форма только в альфе: цвет частице дает tint спрайта. По краю
 * кадра — пустая кайма: при повороте и сглаживании край фигуры не обрезается.
 *
 * Фигура задается функцией прозрачности от точки кадра в координатах от -1 до 1
 * (центр кадра — ноль). Четкие фигуры сглаживаются перебором точек внутри
 * пикселя. Фигуре из двух красок (штрих снежной ленты с тенью) задается еще и
 * функция цвета точки. Рой снежинок — кадр с десятками мелких хлопьев —
 * рисуется иначе: хлопок за хлопком, каждый в своем уголке кадра (функция
 * draw); перебирать все хлопья для каждой точки кадра было бы долго.
 */

const WHITE = [255, 255, 255];
const clamp01 = (value) => Math.min(1, Math.max(0, value));
const smoothstep = (from, to, value) => {
  const share = clamp01((value - from) / (to - from));
  return share * share * (3 - 2 * share);
};
// Мягкое пятно: 1 в центре, плавно в ноль к радиусу 1
const softDisc = (distance, hardness = 2.4) => {
  if (distance >= 1) {
    return 0;
  }
  const edge = Math.exp(-hardness * hardness);
  return (Math.exp(-((distance * hardness) ** 2)) - edge) / (1 - edge);
};

// Клубы дыма и пара: несколько пятен, слитых в одно, — край выходит неровным
const PUFF_LOBES = [
  [0, 0, 0.62], [-0.3, -0.12, 0.42], [0.28, -0.2, 0.44], [0.2, 0.26, 0.4], [-0.24, 0.24, 0.38], [0.02, -0.34, 0.34],
];
// Хлопья пепла: неровный шестиугольник (вершины по кругу, радиусы разные)
const FLAKE_RADII = [0.86, 0.6, 0.8, 0.52, 0.78, 0.64];

// Штрих снежной ленты: белый гребень в верхней половине кадра и серо-голубая
// тень под ним (места гребня и тени поперек кадра, их полуширины, краска и
// плотность тени)
const SNOW_STREAK_CREST = {y: -0.22, halfWidth: 0.3};
const SNOW_STREAK_SHADOW = {y: 0.3, halfWidth: 0.55, alpha: 0.5, color: [86, 112, 134]};
/** Плотности гребня и тени штриха снежной ленты в точке кадра */
const resolveSnowStreakParts = (x, y) => {
  const along = Math.abs(x) / 0.94;
  if (along >= 1) {
    return {crest: 0, shadow: 0};
  }
  // Вдоль штрих сужается и тает к обоим концам
  const taper = Math.cos((along * Math.PI) / 2) ** 0.8;
  const narrowing = 0.4 + 0.6 * taper;
  return {
    crest: softDisc(Math.abs(y - SNOW_STREAK_CREST.y) / (SNOW_STREAK_CREST.halfWidth * narrowing), 2) * taper,
    shadow: softDisc(Math.abs(y - SNOW_STREAK_SHADOW.y) / (SNOW_STREAK_SHADOW.halfWidth * narrowing), 1.8)
      * taper * SNOW_STREAK_SHADOW.alpha,
  };
};

/**
 * Рой снежинок: мелкие округлые хлопья разного размера, чуть сплюснутые (хлопок
 * в полете едва смазан ветром, но не вытянут в «рисовое зерно»). Большинство
 * белые, остальные — светлые серо-голубые: снег в воздухе на фоне белой равнины
 * чуть темнее нее, а на жетоне виден белый. Раскладка — от своего счетчика, а
 * не от Math.random: текстура у всех клиентов и при каждом запуске одна и та же.
 */
const FLURRY_FLAKE_COUNT = 150;
// Хлопья разбросаны по всему квадрату кадра до такого расстояния от его
// середины по каждой оси (доля полукадра); с расстояния RIM_FROM они бледнеют
// и к кромке сходят на нет: у роя нет заметного края, и рои внахлест
// сливаются в ровный снегопад. Квадрат, а не круг: пустые углы кадра роя
// рисовались бы зря
const FLURRY_REACH = 0.9;
const FLURRY_RIM_FROM = 0.66;
const FLURRY_SHADE_SHARE = 0.35;
const FLURRY_SHADE_COLOR = [170, 186, 206];
const buildFlurryFlakes = () => {
  let state = 20261010;
  const next = () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
  const flakes = [];
  for (let index = 0; index < FLURRY_FLAKE_COUNT; index += 1) {
    // Хлопья — по всему квадрату; к кайме кадра рой бледнеет
    const x = (next() * 2 - 1) * FLURRY_REACH;
    const y = (next() * 2 - 1) * FLURRY_REACH;
    const tilt = next() * Math.PI;
    const rim = 1 - smoothstep(FLURRY_RIM_FROM, FLURRY_REACH, Math.max(Math.abs(x), Math.abs(y)));
    // Полуразмер хлопка в долях полукадра: при кадре в 256 точек — от двух до
    // четырех с половиной точек (мелких больше, чем крупных); сплюснут не
    // больше чем в полтора раза
    const radius = 0.016 + next() * next() * 0.024;
    flakes.push({
      x,
      y,
      cos: Math.cos(tilt),
      sin: Math.sin(tilt),
      halfLength: radius * (1 + next() * 0.5),
      halfThickness: radius,
      brightness: (0.65 + next() * 0.35) * rim,
      color: next() < FLURRY_SHADE_SHARE ? FLURRY_SHADE_COLOR : WHITE,
    });
  }
  return flakes;
};
const FLURRY_SAMPLES = 2;
/** Нарисовать рой снежинок: каждый хлопок — в своем уголке кадра */
const drawFlurry = (pixels, width, height, border) => {
  buildFlurryFlakes().forEach((flake) => {
    // Уголок кадра, в который хлопок помещается целиком (в пикселях)
    const reach = flake.halfLength + flake.halfThickness;
    const fromX = Math.max(border, Math.floor(((flake.x - reach + 1) / 2) * width));
    const toX = Math.min(width - border - 1, Math.ceil(((flake.x + reach + 1) / 2) * width));
    const fromY = Math.max(border, Math.floor(((flake.y - reach + 1) / 2) * height));
    const toY = Math.min(height - border - 1, Math.ceil(((flake.y + reach + 1) / 2) * height));
    for (let y = fromY; y <= toY; y += 1) {
      for (let x = fromX; x <= toX; x += 1) {
        let total = 0;
        for (let sampleY = 0; sampleY < FLURRY_SAMPLES; sampleY += 1) {
          for (let sampleX = 0; sampleX < FLURRY_SAMPLES; sampleX += 1) {
            const dx = ((x + (sampleX + 0.5) / FLURRY_SAMPLES) / width) * 2 - 1 - flake.x;
            const dy = ((y + (sampleY + 0.5) / FLURRY_SAMPLES) / height) * 2 - 1 - flake.y;
            const along = (dx * flake.cos + dy * flake.sin) / flake.halfLength;
            const across = (dy * flake.cos - dx * flake.sin) / flake.halfThickness;
            const distanceSquared = along * along + across * across;
            if (distanceSquared < 1) {
              // Середина плотная, край мягкий
              total += (1 - distanceSquared) ** 1.3;
            }
          }
        }
        const alpha = Math.round((255 * flake.brightness * total) / (FLURRY_SAMPLES * FLURRY_SAMPLES));
        const offset = (y * width + x) * 4;
        // Хлопья изредка накладываются: точка остается за тем, что плотнее
        if (alpha > pixels[offset + 3]) {
          pixels[offset] = flake.color[0];
          pixels[offset + 1] = flake.color[1];
          pixels[offset + 2] = flake.color[2];
          pixels[offset + 3] = alpha;
        }
      }
    }
  });
};

// Прядь пыли (вуаль соляной пыли): тонкие волоски бок о бок — место каждого
// поперек кадра и его плотность — и легкая дымка между ними: ровная полоса
// (ее плотность), которая от from до to поперек кадра сходит на нет. Волоски
// идут вдоль всего кадра не сходясь: у соседних кусков пряди, уложенных
// внахлест, они продолжают друг друга. Места волосков — середины строк кадра:
// волосок выходит ровным, в полторы строки. Плотность у волосков разная:
// прядь не выглядит гребенкой
const TRESS_HAIRS = [
  {y: -0.5625, alpha: 1}, {y: -0.1875, alpha: 0.6}, {y: 0.1875, alpha: 0.85}, {y: 0.5625, alpha: 0.7},
];
const TRESS_HAIR_HALF_WIDTH = 0.1;
const TRESS_HAZE = {alpha: 0.3, from: 0.4, to: 0.78};

/**
 * Прядь травяной волны (порыв ветра над лугом): кусок светлой полосы травы, которую ветер положил и повернул
 * к свету. Тонкие короткие стебли лежат вдоль оси Y кадра (по ветру), под ними — слабый мягкий отсвет: из-за
 * стеблей полоса читается травой, а не размытым пятном. Верх кадра — передняя кромка волны: первый ряд стеблей
 * начинается у нее, остальные — глубже, и чем глубже, тем их меньше и тем они бледнее. Раскладка — от своего
 * счетчика, а не от Math.random: текстура у всех клиентов и при каждом запуске одна и та же.
 */
const SWATH_BLADE_COUNT = 120;
// Полуширина стебля (доля полукадра): при кадре в 64 точки — волосок в две точки
const SWATH_BLADE_HALF_WIDTH = 0.034;
// Где у стеблей кончики: у передней кромки (y и разброс вокруг него) и глубже нее — не дальше spread
const SWATH_CREST = {y: -0.56, jitter: 0.12, spread: 0.85};
// Отсвет под стеблями: от front до crest проступает, от crest до tail тает (power — как быстро)
const SWATH_SHEEN = {alpha: 0.3, front: -0.8, crest: -0.4, tail: 0.75, power: 1.3};
const buildSwathBlades = () => {
  let state = 20261011;
  const next = () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
  const blades = [];
  for (let index = 0; index < SWATH_BLADE_COUNT; index += 1) {
    // Глубина стебля в полосе: 0 — у передней кромки, 1 — у задней; у кромки стебли гуще
    const depth = next() ** 1.7;
    blades.push({
      x: (next() * 2 - 1) * 0.9,
      tip: SWATH_CREST.y + (next() * 2 - 1) * SWATH_CREST.jitter + depth * SWATH_CREST.spread,
      // Стебель — от двенадцатой до пятой доли кадра
      length: 0.16 + next() * 0.26,
      alpha: (0.75 + next() * 0.25) * (1 - 0.55 * depth),
    });
  }
  return blades;
};
const SWATH_BLADES = buildSwathBlades();

const SHAPES = {
  [FIELD_BIOME_TEXTURES.DOT]: {
    width: 32, height: 32,
    alphaAt: (x, y) => softDisc(Math.hypot(x, y) / 0.9),
  },
  [FIELD_BIOME_TEXTURES.STREAK]: {
    width: 64, height: 16,
    // Вдоль — сужается к обоим концам, поперек — мягкий гребень
    alphaAt: (x, y) => {
      const along = Math.abs(x) / 0.94;
      if (along >= 1) {
        return 0;
      }
      const taper = Math.cos((along * Math.PI) / 2) ** 0.8;
      return softDisc(Math.abs(y) / (0.78 * (0.35 + 0.65 * taper)), 2) * taper;
    },
  },
  [FIELD_BIOME_TEXTURES.LEAF]: {
    width: 32, height: 16, samples: 3,
    // Заостренный овал: две дуги, сходящиеся в острые концы
    alphaAt: (x, y) => {
      const along = Math.abs(x) / 0.9;
      return along < 1 && Math.abs(y) <= 0.78 * (1 - along * along) ? 1 : 0;
    },
  },
  [FIELD_BIOME_TEXTURES.RING]: {
    width: 128, height: 128,
    alphaAt: (x, y) => Math.exp(-(((Math.hypot(x, y) - 0.8) / 0.075) ** 2)) * (Math.hypot(x, y) < 0.97 ? 1 : 0),
  },
  [FIELD_BIOME_TEXTURES.RIPPLE]: {
    width: 64, height: 64,
    alphaAt: (x, y) => Math.exp(-(((Math.hypot(x, y) - 0.84) / 0.05) ** 2)) * (Math.hypot(x, y) < 0.96 ? 1 : 0),
  },
  [FIELD_BIOME_TEXTURES.STAR]: {
    width: 32, height: 32, samples: 4,
    // Вогнутый ромб: четыре острых луча, перемычка между ними — шестая часть луча
    alphaAt: (x, y) => (Math.abs(x / 0.9) ** 0.42 + Math.abs(y / 0.9) ** 0.42 <= 1 ? 1 : 0),
  },
  [FIELD_BIOME_TEXTURES.SPARK]: {
    width: 32, height: 32,
    // Яркое ядро и два тонких луча крест-накрест, тающих к концам
    alphaAt: (x, y) => {
      const ray = (along, across) => (
        Math.abs(along) < 0.92 ? Math.exp(-((across / 0.07) ** 2)) * (1 - Math.abs(along) / 0.92) ** 1.4 : 0
      );
      return Math.max(softDisc(Math.hypot(x, y) / 0.42, 2), ray(x, y), ray(y, x));
    },
  },
  [FIELD_BIOME_TEXTURES.PUFF]: {
    width: 64, height: 64,
    alphaAt: (x, y) => {
      let density = 0;
      for (let index = 0; index < PUFF_LOBES.length; index += 1) {
        const [lobeX, lobeY, radius] = PUFF_LOBES[index];
        density += softDisc(Math.hypot(x - lobeX, y - lobeY) / radius, 1.9);
      }
      // К кайме кадра клуб обязан сойти на нет
      return clamp01(density * 0.85) * smoothstep(0.96, 0.7, Math.hypot(x, y));
    },
  },
  [FIELD_BIOME_TEXTURES.FLAKE]: {
    width: 16, height: 16, samples: 4,
    alphaAt: (x, y) => {
      const sector = (((Math.atan2(y, x) / (Math.PI * 2)) + 1) % 1) * FLAKE_RADII.length;
      const from = Math.floor(sector) % FLAKE_RADII.length;
      const share = sector - Math.floor(sector);
      const radius = FLAKE_RADII[from] * (1 - share) + FLAKE_RADII[(from + 1) % FLAKE_RADII.length] * share;
      return Math.hypot(x, y) <= radius ? 1 : 0;
    },
  },
  [FIELD_BIOME_TEXTURES.SNOW_STREAK]: {
    width: 64, height: 16,
    // Гребень лежит поверх тени
    alphaAt: (x, y) => {
      const {crest, shadow} = resolveSnowStreakParts(x, y);
      return crest + shadow * (1 - crest);
    },
    colorAt: (x, y) => {
      const {crest, shadow} = resolveSnowStreakParts(x, y);
      const total = crest + shadow * (1 - crest);
      if (!(total > 0)) {
        return WHITE;
      }
      const crestShare = crest / total;
      return WHITE.map((channel, index) => (
        Math.round(channel * crestShare + SNOW_STREAK_SHADOW.color[index] * (1 - crestShare))
      ));
    },
  },
  [FIELD_BIOME_TEXTURES.FLURRY]: {width: 256, height: 256, draw: drawFlurry},
  [FIELD_BIOME_TEXTURES.SNOWFLAKE]: {
    width: 16, height: 16, samples: 4,
    // Тот же хлопок, что в рое снежинок (drawFlurry): середина плотная, край мягкий. Круг в 0.72 полукадра:
    // сплюснутым и повернутым снежинку ставит сама вьюга
    alphaAt: (x, y) => {
      const distanceSquared = (x * x + y * y) / (0.72 * 0.72);
      return distanceSquared < 1 ? (1 - distanceSquared) ** 1.3 : 0;
    },
  },
  [FIELD_BIOME_TEXTURES.RIM_SEGMENT]: {
    // Отрезком рисуют дугу в несколько ячеек длиной: в кадре мельче изгиб пошел бы ступеньками
    width: 128, height: 64,
    // Полоса выгнута дугой: середина — в середине кадра, концы опущены к нижней стороне кадра (туда смотрит
    // центр кольца). Поперек — мягкий гребень одной ширины по всей длине. Вдоль — квадрат косинуса: два
    // отрезка, уложенные с шагом в половину длины, в сумме дают ровно единицу — стыков в ободе не видно
    alphaAt: (x, y) => {
      const along = x / 0.9;
      if (Math.abs(along) >= 1) {
        return 0;
      }
      return Math.cos((along * Math.PI) / 2) ** 2 * softDisc(Math.abs(y - 0.47 * along * along) / 0.42, 2.2);
    },
  },
  [FIELD_BIOME_TEXTURES.FUNNEL]: {
    // Тень размыта, и мелкий кадр ей не во вред; но размер прежний: кадр растягивают на пару ячеек
    width: 128, height: 64,
    alphaAt: (x, y) => {
      // Место вдоль тени: 0 — основание (его ставят под вихрь), 1 — дальний конец раструба. Основание — не у
      // края кадра, а в пятой части его длины от левого края (x = -0.6): слева от основания — запас, на
      // котором тень сходит на нет. Дальний конец — у правого края (x = 0.9). Те же места записаны у вихря —
      // shadow.frame в configs/biomeEffects/dustDevil: по ним он ставит основание тени под себя
      const along = (x + 0.6) / 1.5;
      if (along <= -0.2 || along >= 1) {
        return 0;
      }
      const reach = clamp01(along);
      // Столб чуть выгнут дугой; полуширина растет от основания к раструбу
      const axis = 0.06 * Math.sin(Math.PI * reach);
      const halfWidth = 0.42 + 0.44 * reach;
      // Четкой кромки у тени нет нигде (просьба владельца игры: прежняя воронка была слишком резкой). Поперек
      // тень тает от самой оси до края; вдоль — проступает на запасе перед основанием, темнее всего от
      // основания до трети длины и на остальных двух третях плавно сходит на нет
      const body = 1 - smoothstep(0.08, 1, Math.abs(y - axis) / halfWidth);
      return body * smoothstep(-0.2, 0, along) * (1 - smoothstep(0.35, 1, along));
    },
  },
  [FIELD_BIOME_TEXTURES.TRESS]: {
    width: 64, height: 16,
    alphaAt: (x, y) => {
      const along = Math.abs(x) / 0.94;
      if (along >= 1) {
        return 0;
      }
      // Точка — на самом плотном из того, что через нее проходит: волоски поверх дымки
      let density = TRESS_HAZE.alpha * (1 - smoothstep(TRESS_HAZE.from, TRESS_HAZE.to, Math.abs(y)));
      for (let index = 0; index < TRESS_HAIRS.length; index += 1) {
        const hair = TRESS_HAIRS[index];
        density = Math.max(density, Math.exp(-(((y - hair.y) / TRESS_HAIR_HALF_WIDTH) ** 2)) * hair.alpha);
      }
      // К обоим концам кадра прядь тает, не сужаясь, — плавно с обеих сторон: куски пряди, уложенные
      // внахлест вдвое, вместе дают ровную плотность без швов
      return density * Math.cos((along * Math.PI) / 2) ** 2;
    },
  },
  [FIELD_BIOME_TEXTURES.LENS]: {
    width: 64, height: 16,
    // Две дуги, сходящиеся в острые концы; от плотной середины к кромке линза плавно тает
    alphaAt: (x, y) => {
      const along = Math.abs(x) / 0.92;
      if (along >= 1) {
        return 0;
      }
      return 1 - smoothstep(0.35, 1, Math.abs(y) / (0.72 * (1 - along * along)));
    },
  },
  [FIELD_BIOME_TEXTURES.CLOT]: {
    width: 16, height: 16, samples: 4,
    // Сплошной круг в 0.72 полукадра: край ровный, сглажен только перебором точек внутри пикселя
    alphaAt: (x, y) => (Math.hypot(x, y) <= 0.72 ? 1 : 0),
  },
  [FIELD_BIOME_TEXTURES.SWATH]: {
    width: 64, height: 64, samples: 3,
    alphaAt: (x, y) => {
      const along = Math.abs(x) / 0.9;
      if (along >= 1 || Math.abs(y) >= 0.9) {
        return 0;
      }
      // Точка — на самом плотном из того, что через нее проходит: стебли поверх отсвета
      let density = SWATH_SHEEN.alpha * smoothstep(SWATH_SHEEN.front, SWATH_SHEEN.crest, y)
        * (1 - smoothstep(SWATH_SHEEN.crest, SWATH_SHEEN.tail, y)) ** SWATH_SHEEN.power;
      for (let index = 0; index < SWATH_BLADES.length; index += 1) {
        const blade = SWATH_BLADES[index];
        // Место точки на стебле: 0 — кончик (к передней кромке волны), 1 — основание
        const share = (y - blade.tip) / blade.length;
        if (share > 0 && share < 1 && Math.abs(x - blade.x) < SWATH_BLADE_HALF_WIDTH * 3) {
          // Кончик четкий, к основанию стебель тает
          const body = smoothstep(0, 0.1, share) * (1 - share) ** 1.4;
          density = Math.max(density, blade.alpha * body * Math.exp(-(((x - blade.x) / SWATH_BLADE_HALF_WIDTH) ** 2)));
        }
      }
      // Вдоль полосы — квадрат косинуса: пряди, уложенные внахлест с шагом в половину длины, вместе дают ровную
      // полосу без швов. К нижней стороне кадра все сходит на нет
      return density * Math.cos((along * Math.PI) / 2) ** 2 * (1 - smoothstep(0.75, 0.9, Math.abs(y)));
    },
  },
};

/** Размер текстуры эффекта в пикселях; null — такой текстуры нет */
export const getBiomeTextureSize = (key) => (
  Object.prototype.hasOwnProperty.call(SHAPES, key) ? {width: SHAPES[key].width, height: SHAPES[key].height} : null
);

/**
 * Пиксели текстуры эффекта.
 * @param {string} key — ключ из FIELD_BIOME_TEXTURES
 * @returns {Uint8Array|null} RGBA; null — такой текстуры нет
 */
export const buildBiomeTexturePixels = (key) => {
  if (!Object.prototype.hasOwnProperty.call(SHAPES, key)) {
    return null;
  }
  const {width, height, alphaAt, colorAt, draw, samples = 2} = SHAPES[key];
  const pixels = new Uint8Array(width * height * 4);
  // Кайма в два пикселя остается пустой при любой фигуре
  const border = 2;
  if (draw) {
    draw(pixels, width, height, border);
    return pixels;
  }
  for (let y = border; y < height - border; y += 1) {
    for (let x = border; x < width - border; x += 1) {
      let total = 0;
      for (let sampleY = 0; sampleY < samples; sampleY += 1) {
        for (let sampleX = 0; sampleX < samples; sampleX += 1) {
          // Точка кадра в координатах -1..1 по обеим осям
          const pointX = ((x + (sampleX + 0.5) / samples) / width) * 2 - 1;
          const pointY = ((y + (sampleY + 0.5) / samples) / height) * 2 - 1;
          total += clamp01(alphaAt(pointX, pointY));
        }
      }
      const alpha = total / (samples * samples);
      if (alpha > 0) {
        // Цвет 0..255 как есть (на альфу его умножит браузер), альфа 0..1.
        // У фигуры из двух красок цвет — в середине пикселя
        const [red, green, blue] = colorAt
          ? colorAt(((x + 0.5) / width) * 2 - 1, ((y + 0.5) / height) * 2 - 1)
          : WHITE;
        const offset = (y * width + x) * 4;
        pixels[offset] = red;
        pixels[offset + 1] = green;
        pixels[offset + 2] = blue;
        pixels[offset + 3] = Math.round(255 * alpha);
      }
    }
  }
  return pixels;
};

/**
 * Зарегистрировать текстуры эффекта в сцене (готовые не трогает).
 *
 * @param {Phaser.Scene} scene
 * @param {string[]} keys — ключи из FIELD_BIOME_TEXTURES
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR: у текстуры
 *   из массива по умолчанию NEAREST, и повернутая частица пошла бы ступеньками
 * @returns {boolean} все ли текстуры на месте; false — эффекта не будет
 */
export const ensureBiomeTextures = (scene, keys, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }
  return keys.every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const pixels = buildBiomeTexturePixels(key);
    if (!pixels) {
      return false;
    }
    const texture = textures.addUint8Array(key, pixels, SHAPES[key].width, SHAPES[key].height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
